import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronRight } from 'lucide-react';
import { useUserStore } from '../../lib/userStore';
import { useThemeStore } from '../../lib/themeStore';
import { supabase } from '../../lib/supabase';
import { isOnboardingDisabled } from '../../lib/onboarding';

// Must match callback.ts
const BPM_LEAGUE_ID = 138;

// ─── Types ────────────────────────────────────────────────────────────────────

type OnboardingStep = 'welcome' | 'building' | 'tour' | 'done';

interface TourCardDef {
  emoji: string;
  title: string;
  subtitle: string;
  path?: string;
  cta: string;
}

// ─── Tour cards ───────────────────────────────────────────────────────────────

const TOUR_CARDS: TourCardDef[] = [
  {
    emoji: '🎉',
    title: 'Ton équipe est prête !',
    subtitle:
      'Excellente décision. Tu es maintenant dans la partie. Voici les sections essentielles pour bien débuter.',
    cta: 'Découvrir →',
  },
  {
    emoji: '🏒',
    title: 'Mon équipe',
    subtitle:
      'Gère ton alignement — attaquants, défenseurs, gardiens, réserve et LTIR (joueurs blessés).',
    path: '/my-teams',
    cta: 'Voir mon équipe',
  },
  {
    emoji: '📊',
    title: 'Classement',
    subtitle: 'Consulte ta position dans la ligue et les points accumulés par chaque équipe.',
    path: '/standings',
    cta: 'Voir le classement',
  },
  {
    emoji: '👤',
    title: 'Agents libres',
    subtitle:
      'Signe de nouveaux joueurs disponibles pour renforcer ton équipe tout au long de la saison.',
    path: '/free-agents',
    cta: 'Voir les agents libres',
  },
];

// ─── Styles helpers ───────────────────────────────────────────────────────────

const BPM_ORANGE = '#f5a623';

function useColors(isDark: boolean) {
  return {
    bgCard: isDark ? '#1e293b' : '#ffffff',
    bgCardBorder: isDark ? `${BPM_ORANGE}25` : `${BPM_ORANGE}40`,
    textPrimary: isDark ? '#f1f5f9' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    dotInactive: isDark ? '#334155' : '#e2e8f0',
    stepInactive: isDark ? '#1e3a5f' : '#e2e8f0',
  };
}

// ─── Progress steps (header of welcome/tour) ──────────────────────────────────

function ProgressSteps({
  current,
  labels,
  colors,
}: {
  current: number;
  labels: string[];
  colors: ReturnType<typeof useColors>;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '4px', marginBottom: '1.5rem' }}>
      {labels.map((label, i) => {
        const isPast = i < current;
        const isActive = i === current;
        return (
          <React.Fragment key={i}>
            <div style={{ textAlign: 'center', minWidth: '64px' }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: isActive ? BPM_ORANGE : isPast ? `${BPM_ORANGE}70` : colors.stepInactive,
                  color: isActive || isPast ? '#000' : colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  margin: '0 auto 4px',
                  transition: 'background-color 0.3s',
                }}
              >
                {isPast ? '✓' : i + 1}
              </div>
              <span
                style={{
                  fontSize: '10px',
                  color: isActive ? BPM_ORANGE : colors.textSecondary,
                  fontWeight: isActive ? 600 : 400,
                  lineHeight: 1.2,
                  display: 'block',
                }}
              >
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  backgroundColor: isPast ? BPM_ORANGE : colors.stepInactive,
                  marginTop: 13,
                  borderRadius: 2,
                  transition: 'background-color 0.3s',
                  maxWidth: 40,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

export function BpmOnboardingWizard() {
  const isBpmSession = useUserStore((s) => s.is_bpm_session);
  const userId = useUserStore((s) => s.user_id);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useThemeStore();

  const isDark = theme === 'dark';
  const colors = useColors(isDark);
  const storageKey = userId != null ? `bpm-onboarding-v1-${userId}` : null;

  // Allow forcing the BPM onboarding in local/dev via Vite env var
  const FORCE_BPM_ONBOARDING = (import.meta.env as any).VITE_BPM_FORCE_ONBOARDING === 'true';
  const effectiveIsBpmSession = isBpmSession || FORCE_BPM_ONBOARDING;

  // Global disable guard: if onboarding is disabled, never render
  if (isOnboardingDisabled()) return null;

  const [step, setStep] = useState<OnboardingStep>(() => {
    if (typeof window === 'undefined') return 'welcome';
    // getState() reads the persisted store synchronously (works on refresh)
    const persistedId = useUserStore.getState().user_id;
    if (persistedId == null) return 'welcome'; // fresh arrival — userId not set yet
    const saved = localStorage.getItem(`bpm-onboarding-v1-${persistedId}`) as OnboardingStep | null;
    return saved ?? 'welcome';
  });

  // Re-read saved step from localStorage when userId becomes available (fresh BPM arrival)
  useEffect(() => {
    if (userId == null) return;
    const saved = localStorage.getItem(`bpm-onboarding-v1-${userId}`) as OnboardingStep | null;
    if (saved) setStep(saved);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [tourCard, setTourCard] = useState(0);
  // Banner can be minimized without fully closing the tutorial
  const [bannerMinimized, setBannerMinimized] = useState(false);
  // When navigating from a tour CTA to a page, hide the modal so the
  // page can show its anchored highlight. This is transient and
  // doesn't mark the tutorial as completed.
  const [modalHidden, setModalHidden] = useState(false);
  // True while the initial DB check runs (prevents flickering a wrong state)
  const [isCheckingBuild, setIsCheckingBuild] = useState(true);
  // Track window width to offset banner past the sidebar on desktop
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0
  );

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Initial check: is the team already built? (independent of league type) ───
  useEffect(() => {
    if (!effectiveIsBpmSession) {
      setIsCheckingBuild(false);
      return;
    }

    // If onboarding is forced for testing and there's no userId yet,
    // skip the DB check and allow the wizard to show immediately.
    if (FORCE_BPM_ONBOARDING && !userId) {
      setIsCheckingBuild(false);
      return;
    }
    // Reset to true so no flash occurs between isBpmSession becoming true and DB response
    setIsCheckingBuild(true);

    let cancelled = false;

    const check = async () => {
      try {
        const { data, error } = await supabase
          .from('leagues_users')
          .select('initial_build_complete')
          .eq('league_id', BPM_LEAGUE_ID)
          .eq('user_id', userId)
          .single();

        if (cancelled) return;
        if (error && error.code !== 'PGRST116') return;

        // Stale closure on `step` is fine: step is 'welcome' or 'building' on mount
        if (data?.initial_build_complete && (step === 'welcome' || step === 'building')) {
          setStep('done');
        }
      } finally {
        if (!cancelled) setIsCheckingBuild(false);
      }
    };

    check();
    return () => { cancelled = true; };
  }, [effectiveIsBpmSession, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Detect build completion while in building step ───────────────────────────
  // Re-check initial_build_complete on each navigation; this catches
  // the post-completion navigation fired by BuildMyTeam.
  useEffect(() => {
    if (!effectiveIsBpmSession || !userId || step !== 'building') return;

    let cancelled = false;

    const checkCompletion = async () => {
      const { data, error } = await supabase
        .from('leagues_users')
        .select('initial_build_complete')
        .eq('league_id', BPM_LEAGUE_ID)
        .eq('user_id', userId)
        .single();

      if (cancelled || (error && error.code !== 'PGRST116')) return;
      if (data?.initial_build_complete) {
        setStep('tour');
        setTourCard(0);
      }
    };

    checkCompletion();
    return () => { cancelled = true; };
  }, [location.pathname, effectiveIsBpmSession, userId, step]);

  // ── Persist step to localStorage ─────────────────────────────────────────────
  useEffect(() => {
    if (!storageKey) return;
    if (step === 'done') {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, step);
    }
  }, [step, storageKey]);

  // When navigation occurs, if the location.state contains a
  // `bpmOnboardingTarget` index, ensure we're in the `tour` step and
  // show that specific card. This allows target pages to display an
  // anchored highlight when the user clicks a CTA from the tour.
  useEffect(() => {
    // location.state can be anything; guard access
    const maybeState = (location.state as any) ?? null;
    if (maybeState && typeof maybeState.bpmOnboardingTarget === 'number') {
      const idx = maybeState.bpmOnboardingTarget as number;
      if (idx >= 0 && idx < TOUR_CARDS.length) {
        setTourCard(idx);
        setStep('tour');
        // keep modal hidden when arriving from a tour CTA so the
        // page-level highlight is visible
        setModalHidden(true);
        return;
      }
    }
    // If navigation did not include a bpmOnboardingTarget, ensure the
    // modal is visible again (not transiently hidden).
    setModalHidden(false);
    // We intentionally depend on location.key so this runs on every
    // navigation event (React Router updates key on navigation).
  }, [location.key]);

  // ── Early exit ───────────────────────────────────────────────────────────────
  // When forced onboarding is active, ignore the DB "isCheckingBuild" state
  // so the tutorial can appear immediately for testing. We no longer
  // return early when `modalHidden` is true: instead we render a small
  // resume button so the user can reopen the modal and continue the tour.
  if (!effectiveIsBpmSession || (isCheckingBuild && !FORCE_BPM_ONBOARDING) || step === 'done') return null;

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleStartBuilding = () => {
    setStep('building');
    setBannerMinimized(false);
    if (location.pathname !== '/build-my-team') {
      navigate('/build-my-team');
    }
  };

  const handleSkipAll = () => setStep('done');

  const handleNextTourCard = (path?: string) => {
    // If the card has a path, navigate there and include the current
    // tour card index in location.state so the destination page can
    // react (anchor/highlight) and the wizard can re-sync when the
    // route changes. We intentionally do NOT advance the card here.
    if (path) {
      // navigate and hide the modal so the destination page's anchor is visible
      navigate(path, { state: { bpmOnboardingTarget: tourCard } });
      setModalHidden(true);
      // do not advance the card automatically
      return;
    }

    if (tourCard < TOUR_CARDS.length - 1) {
      setTourCard((p) => p + 1);
    } else {
      setStep('done');
    }
  };

  // ── Backdrop ─────────────────────────────────────────────────────────────────

  const backdrop: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 9000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.75)',
    backdropFilter: 'blur(4px)',
    padding: '1rem',
  };

  const cardBase: React.CSSProperties = {
    backgroundColor: colors.bgCard,
    borderRadius: '16px',
    padding: '2rem',
    maxWidth: '460px',
    width: '100%',
    boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
    border: `1px solid ${colors.bgCardBorder}`,
    position: 'relative',
  };

  // ── Welcome ──────────────────────────────────────────────────────────────────

  if (step === 'welcome') {
    return (
      <div style={backdrop} role="dialog" aria-modal="true" aria-label="Bienvenue sur ton pool BPM Sports">
        <div style={cardBase}>
          <button
            onClick={handleSkipAll}
            aria-label="Fermer le tutoriel"
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'none',
              border: 'none',
              color: colors.textSecondary,
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={18} />
          </button>

          <ProgressSteps
            current={0}
            labels={['Bienvenue', 'Mon équipe', 'Prêt !']}
            colors={colors}
          />

          <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem', lineHeight: 1 }}>🏒</div>
            <h2
              style={{
                color: colors.textPrimary,
                fontWeight: 700,
                fontSize: '1.35rem',
                marginBottom: '0.6rem',
                lineHeight: 1.3,
              }}
            >
              Bienvenue sur ton pool BPM Sports !
            </h2>
            <p style={{ color: colors.textSecondary, fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
              Ton compte est prêt. Il ne te reste qu'à créer ton équipe pour démarrer.
            </p>
          </div>

          <button
            onClick={handleStartBuilding}
            style={{
              width: '100%',
              padding: '0.85rem 1rem',
              backgroundColor: BPM_ORANGE,
              color: '#000',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: '1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            Créer mon équipe <ChevronRight size={20} />
          </button>

          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              onClick={handleSkipAll}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textSecondary,
                fontSize: '0.8rem',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Passer le tutoriel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If the modal was hidden to reveal a page highlight, render a small
  // resume button so the user can re-open the wizard and continue.
  if (modalHidden && step !== 'done') {
    const isLastCard = tourCard === TOUR_CARDS.length - 1;
    if (isLastCard) {
      return (
        <button
          aria-label="Tutoriel terminé — Retour à l'accueil"
          title="Félicitations — Retour à l'accueil"
          onClick={() => {
            setStep('done');
            navigate('/my-office');
          }}
          style={{
            position: 'fixed',
            right: 16,
            bottom: 20,
            zIndex: 8500,
            backgroundColor: BPM_ORANGE,
            color: '#000',
            border: 'none',
            borderRadius: 10,
            padding: '8px 12px',
            boxShadow: '0 6px 18px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            fontWeight: 700,
          }}
        >
          Félicitations — Tutoriel terminé · Retour à l'accueil
        </button>
      );
    }

    const nextIdx = Math.min(tourCard + 1, TOUR_CARDS.length - 1);
    const nextLabel = TOUR_CARDS[nextIdx]?.title ?? 'Suivant';
    const handleResumeNext = () => {
      // Re-open the modal and advance to the next explanation card
      setModalHidden(false);
      if (nextIdx !== tourCard) setTourCard(nextIdx);
    };

    return (
      <button
        aria-label="Passer à la prochaine explication"
        title={`Passer à la prochaine explication — ${nextLabel}`}
        onClick={handleResumeNext}
        style={{
          position: 'fixed',
          right: 16,
          bottom: 20,
          zIndex: 8500,
          backgroundColor: BPM_ORANGE,
          color: '#000',
          border: 'none',
          borderRadius: 10,
          padding: '8px 12px',
          boxShadow: '0 6px 18px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          fontWeight: 700,
        }}
      >
        Passer à la prochaine explication — {nextLabel}
      </button>
    );
  }

  // ── Building banner ──────────────────────────────────────────────────────────

  if (step === 'building') {
    // Minimized state: floating resume button
    if (bannerMinimized) {
      return (
        <button
          onClick={() => setBannerMinimized(false)}
          aria-label="Reprendre le guide de démarrage"
          title="Reprendre le guide de démarrage"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            zIndex: 8000,
            width: 52,
            height: 52,
            borderRadius: '50%',
            backgroundColor: BPM_ORANGE,
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.4rem',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          🏒
        </button>
      );
    }

    const onBuildPage = location.pathname === '/build-my-team';
    // On desktop (≥1200px) the sidebar is 280px wide — push the banner past it
    const sidebarOffset = windowWidth >= 1200 ? 280 : 0;

    return (
      <div
        role="status"
        aria-label="Étape 2 — Construis ton équipe"
        style={{
          position: 'fixed',
          bottom: 0,
          left: sidebarOffset,
          right: 0,
          zIndex: 990,
          backgroundColor: BPM_ORANGE,
          color: '#000',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.25)',
        }}
      >
        {/* Progress + label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexShrink: 0 }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: i === 1 ? 22 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: i === 1 ? '#000' : 'rgba(0,0,0,0.3)',
                  transition: 'width 0.2s',
                }}
              />
            ))}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.2 }}>
              Étape 2 sur 3 — Construis ton équipe
            </div>
            <div style={{ fontSize: '0.75rem', opacity: 0.75, lineHeight: 1.2 }}>
              {onBuildPage
                ? 'Signe tes joueurs, puis clique sur «\u00a0Terminer\u00a0» pour finaliser.'
                : 'Retourne sur la page de construction pour créer ton équipe.'}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {!onBuildPage && (
            <button
              onClick={() => navigate('/build-my-team')}
              style={{
                background: '#000',
                color: '#fff',
                border: 'none',
                borderRadius: '7px',
                padding: '5px 14px',
                fontWeight: 700,
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap',
              }}
            >
              Créer mon équipe <ChevronRight size={14} />
            </button>
          )}
          <button
            onClick={() => setBannerMinimized(true)}
            aria-label="Réduire le guide"
            style={{
              background: 'rgba(0,0,0,0.15)',
              border: 'none',
              color: '#000',
              cursor: 'pointer',
              padding: '5px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={15} />
          </button>
        </div>
      </div>
    );
  }

  // ── Tour ─────────────────────────────────────────────────────────────────────

  if (step === 'tour') {
    const card = TOUR_CARDS[tourCard];
    const isLast = tourCard === TOUR_CARDS.length - 1;

    return (
      <div style={backdrop} role="dialog" aria-modal="true" aria-label={card.title}>
        <div style={{ ...cardBase, maxWidth: '420px' }}>
          {/* Dot progress */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '1.5rem' }}>
            {TOUR_CARDS.map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === tourCard ? 22 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: i === tourCard ? BPM_ORANGE : colors.dotInactive,
                  transition: 'width 0.25s ease, background-color 0.25s ease',
                }}
              />
            ))}
          </div>

          {/* Content */}
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '3.2rem', marginBottom: '0.75rem', lineHeight: 1 }}>{card.emoji}</div>
            <h3
              style={{
                color: colors.textPrimary,
                fontWeight: 700,
                fontSize: '1.25rem',
                marginBottom: '0.5rem',
                lineHeight: 1.3,
              }}
            >
              {card.title}
            </h3>
            <p style={{ color: colors.textSecondary, fontSize: '0.88rem', lineHeight: 1.65, margin: 0 }}>
              {card.subtitle}
            </p>
          </div>

          {/* CTA */}
          <button
            onClick={() => handleNextTourCard(card.path)}
            style={{
              width: '100%',
              padding: '0.8rem 1rem',
              backgroundColor: BPM_ORANGE,
              color: '#000',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: '0.95rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            {card.cta}
            {!isLast && <ChevronRight size={18} />}
          </button>

          {card.path === '/free-agents' && (
            <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
              <button
                  onClick={() => {
                  // Complete the tutorial, then go to my-office
                  setStep('done');
                  navigate('/my-office');
                }}
                style={{
                  marginTop: 8,
                  background: 'none',
                  border: '1px solid ' + (isDark ? '#ffffff33' : '#00000020'),
                  color: colors.textPrimary,
                  padding: '6px 10px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                Voir mon dashboard
              </button>
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: '0.875rem' }}>
            <button
              onClick={handleSkipAll}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textSecondary,
                fontSize: '0.78rem',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              {isLast ? 'Fermer' : 'Passer'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
